import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { Typography } from '@mui/material'
import { ProtoSelectStandalone } from 'src/components/flow-editor/ProtoSelectStandalone'
import { getCustomers } from 'src/selectors/customer'
import { resolveCustomerDisplayName } from 'src/utils/customerList'

interface Props<T> {
  name: keyof T
  label: string
  /**
   * partnerId → reason suffix, e.g. "onboarding in progress". `ProtoSelectStandalone` has no
   * per-option disabled state, so blocked partners stay selectable with the reason in their label
   * and the form's validator rejects them.
   */
  blockedPartnerReasons?: Record<string, string>
  dataTestId?: string
  /** Optional, so existing callers are unaffected. */
  disabled?: boolean
}

/** A searchable partner select bound to a FLAT Formik field holding the partner id. */
const FormikPartnerSelectComponent = <T extends unknown>({
  name,
  label,
  blockedPartnerReasons,
  dataTestId,
  disabled = false,
}: Props<T>) => {
  const customers = useSelector(getCustomers)
  const { values, touched, errors, setFieldValue, setFieldTouched } = useFormikContext<T>()

  const options = useMemo(() => {
    return customers.map((customer) => {
      const reason = blockedPartnerReasons?.[customer.id]
      const displayName = resolveCustomerDisplayName(customer)

      return { value: customer.id, label: reason ? `${displayName} — ${reason}` : displayName }
    })
  }, [blockedPartnerReasons, customers])

  const handleChange = useCallback(
    (nextValue: string | number) => {
      // Validate with the new value here: `setFieldTouched` would validate Formik's previous
      // values, so a just-fixed choice would keep its old error until the next blur.
      setFieldValue(name as string, String(nextValue), true)
      setFieldTouched(name as string, true, false)
    },
    [name, setFieldTouched, setFieldValue],
  )

  const isTouched = Boolean(touched[name])
  const error = errors[name] as string | undefined

  return (
    <div data-testid={dataTestId} css={tw`flex flex-col`}>
      <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
        {label}
      </Typography>
      <ProtoSelectStandalone
        value={(values[name] as unknown as string) ?? ''}
        options={options}
        placeholder="Select a partner"
        searchable
        disabled={disabled}
        searchPlaceholder="Search partners…"
        onValueChange={handleChange}
      />
      {isTouched && error ? (
        <Typography variant="body2" css={tw`mt-1 text-xs text-critical-500`}>
          {error}
        </Typography>
      ) : null}
    </div>
  )
}

// `memo` erases the generic; the cast restores it so callers keep `name: keyof T` checking.
export const FormikPartnerSelect = memo(
  FormikPartnerSelectComponent,
) as typeof FormikPartnerSelectComponent
